import React, { useEffect, useState } from "react";
import { useTheme } from "@mui/material/styles";
import DashboardLayout from "../../components/layout/DashboardLayout";
import BackToDashboard from "../../components/common/BackToDashboard";
import { setPageTitle } from "../../utils/document";
import { getDossierHtml } from "../../api/dossier";

// Dossier is maintained by the Governance Office in SharePoint — the API fetches
// it on every visit so edits there show up here without the HTML being part of
// this app. SharePoint refuses to be iframed directly, so the HTML is rendered via
// srcDoc in a sandboxed frame (no same-origin, so it can't touch PSI360).
export const DOSSIER_URL =
  "https://psiog0.sharepoint.com/:u:/s/GovernanceOffice/IQB0H5bSbkYiTZWt8g8hWlL7AZd2OZAYVPWdvkY-MFPwNak?e=Ed9rfl";

const ExternalLinkIcon = ({ color }) => (
  <svg width="13" height="13" viewBox="0 0 16 16" fill="none"
    stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M7 3H3a1 1 0 0 0-1 1v9a1 1 0 0 0 1 1h9a1 1 0 0 0 1-1V9" />
    <polyline points="10 1 15 1 15 6" />
    <line x1="15" y1="1" x2="7" y2="9" />
  </svg>
);

const DossierPage = () => {
  const theme = useTheme();
  const t     = theme.custom;

  const [html,    setHtml]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => { setPageTitle("PSI360 Dossier"); }, []);

  useEffect(() => {
    let cancelled = false;
    getDossierHtml()
      .then(text => { if (!cancelled) setHtml(text); })
      .catch(err => {
        console.error("Failed to load dossier:", err);
        if (!cancelled) setError("Unable to load the dossier. Use Open in SharePoint to view it.");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return (
    <DashboardLayout>
      <div style={{
        display:       "flex",
        flexDirection: "column",
        height:        "100%",
        padding:       24,
        boxSizing:     "border-box",
        background:    t.bg,
        gap:           16,
      }}>

        {/* Back + page title + open in SharePoint */}
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between" }}>
          <div>
            <div style={{ marginBottom: 10 }}>
              <BackToDashboard />
            </div>
            <div style={{
              fontSize:      15,
              fontWeight:    600,
              color:         t.text,
              letterSpacing: "-0.01em",
            }}>
              PSI360 Dossier
            </div>
          </div>

          <a
            href={DOSSIER_URL}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display:        "inline-flex",
              alignItems:     "center",
              gap:            5,
              padding:        "4px 10px",
              borderRadius:   6,
              background:     t.accent2,
              border:         `1px solid ${t.accent}44`,
              textDecoration: "none",
              whiteSpace:     "nowrap",
            }}
          >
            <ExternalLinkIcon color={t.accent} />
            <span style={{
              fontSize: 11, fontWeight: 600, letterSpacing: ".05em",
              textTransform: "uppercase", color: t.accent,
            }}>
              Open in SharePoint
            </span>
          </a>
        </div>

        {/* Embedded dossier — fills remaining height */}
        {error ? (
          <div style={{ fontSize: 12, color: "#c84040" }}>{error}</div>
        ) : loading ? (
          <div style={{ fontSize: 12, color: t.text3 }}>Loading dossier...</div>
        ) : (
          <iframe
            title="PSI360 Dossier"
            srcDoc={html}
            sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox"
            style={{
              flex:         1,
              minHeight:    0,
              width:        "100%",
              border:       `1px solid ${t.border2}`,
              borderRadius: 6,
              background:   "#fff",
            }}
          />
        )}
      </div>
    </DashboardLayout>
  );
};

export default DossierPage;
